import { merge } from "lodash";
import { useMemo } from "react";

export const WHERE_OPERATORS = {
  EQUAL: "==",
  NOT_EQUAL: "!=",
  LESS_THAN: "<",
  LESS_THAN_OR_EQUAL: "<=",
  GREATER_THAN: ">",
  GREATER_THAN_OR_EQUAL: ">=",
  ARRAY_CONTAINS: "array-contains",
  ARRAY_CONTAINS_ANY: "array-contains-any",
  IN: "in",
  NOT_IN: "not-in",
};

export const getDataFromDocument = (doc) => ({ id: doc.id, ...doc.data() });

export const checkDocument =
  ({ db, doc, getDoc }, path) =>
  async (id) => {
    const docSnap = await getDoc(doc(db, ...path, id));

    return docSnap.exists();
  };

export const addDocument =
  ({ db, addDoc, collection, getDoc }, path) =>
  async (data) => {
    let docRef;

    try {
      docRef = await addDoc(collection(db, ...path), data);
      console.log("Document written with ID: ", docRef.id);
    } catch (e) {
      console.error("Error adding document: ", e);
    }

    const docSnap = await getDoc(docRef);

    return getDataFromDocument(docSnap);
  };

export const addDocumentWithId =
  ({ db, setDoc, doc, getDoc }, path) =>
  async (id, data) => {
    try {
      await setDoc(doc(db, ...path, id), data);
      console.log("Document written with ID: ", id);
    } catch (e) {
      console.error("Error adding document: ", e);
    }

    const docSnap = await getDoc(doc(db, "users", id));

    return getDataFromDocument(docSnap);
  };

export const addIgnoreDocumentWithId =
  (dbContext, path) =>
  async (id, data, { disableReadExisting } = {}) => {
    const { db, getDoc, doc } = dbContext;
    const docSnap = await getDoc(doc(db, ...path, id));

    if (!docSnap.exists()) {
      return await addDocumentWithId(dbContext, path)(id, data);
    }

    if (disableReadExisting) {
      return null;
    }

    return await readDocument(dbContext, path)(id);
  };

export const getAllDocuments =
  ({ db, getDocs, collection }, path) =>
  async () => {
    try {
      const querySnapshot = await getDocs(collection(db, ...path));

      return querySnapshot.docs.map((doc) => getDataFromDocument(doc));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

export const getDocumentsById = (dbContext, path) => async (ids) => {
  const promises = ids.map((id) => {
    return readDocument(dbContext, path)(id);
  });

  try {
    return await Promise.all(promises);
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const getDocumentsWhere =
  ({ db, getDocs, query, collection, where }, path) =>
  async (a, operator, b) => {
    try {
      const querySnapshot = await getDocs(
        query(collection(db, ...path), where(a, operator, b))
      );

      if (querySnapshot.empty) {
        return [];
      }

      return querySnapshot.docs.map((doc) => getDataFromDocument(doc));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

export const readDocument =
  ({ db, getDoc, doc }, path) =>
  async (id) => {
    if (!id) {
      return null;
    }

    const docSnap = await getDoc(doc(db, ...path, id));

    if (!docSnap.exists()) {
      // docSnap.data() will be undefined in this case
      console.log("No such document!", id);
      return null;
    }

    return getDataFromDocument(docSnap);
  };

export const updateDocument =
  ({ db, runTransaction, doc }, path) =>
  async (id, updater) => {
    await runTransaction(db, async (transaction) => {
      const docRef = doc(db, ...path, id);

      const targetDoc = await transaction.get(docRef);

      if (!targetDoc.exists()) {
        throw new Error("Document does not exist!");
      }

      const newData = await updater(targetDoc);

      transaction.update(docRef, { ...newData });
    });
  };
export const updateDocSync =
  ({ db, updateDoc, doc }, path) =>
  async (id, data) => {
    await updateDoc(doc(db, ...path, id), data);
  };
export const updateLatestDocSync =
  (dbContext, path) =>
  async (id, updater, { shouldMergeUpdateAndData } = {}) => {
    const { db, getDoc, doc } = dbContext;
    const latestDoc = await getDoc(doc(db, ...path, id));

    const data = await updater(latestDoc);

    await updateDocSync(dbContext, path)(id, data);

    return shouldMergeUpdateAndData
      ? merge({}, getDataFromDocument(latestDoc), data)
      : undefined;
  };

export const updateOrAddDocumentWithId =
  (dbContext, path) => async (id, data) => {
    const { db, getDoc, doc } = dbContext;
    const docSnap = await getDoc(doc(db, ...path, id));

    if (!docSnap.exists()) {
      return addDocumentWithId(db, path)(id, data);
    }

    return updateDocument(dbContext, path)(id, () => data);
  };

export const subscribeToDocument =
  ({ onSnapshot, doc, db }, path) =>
  (id, callback) => {
    return onSnapshot(doc(db, ...path, id), {}, (doc) => {
      callback(doc);
    });
  };

export const deleteDocument =
  ({ db, deleteDoc, doc }, path) =>
  async (id) => {
    return await deleteDoc(doc(db, ...path, id));
  };

export const useMainAccessors = (dbContext, path) => {
  return useMemo(
    () => ({
      check: checkDocument(dbContext, path),
      add: addDocument(dbContext, path),
      addWithId: addDocumentWithId(dbContext, path),
      // add only if it doesn't exist
      addIgnoreWithId: addIgnoreDocumentWithId(dbContext, path),
      getAll: getAllDocuments(dbContext, path),
      getWhere: getDocumentsWhere(dbContext, path),
      read: readDocument(dbContext, path),
      readMultiple: getDocumentsById(dbContext, path),
      update: updateDocument(dbContext, path),
      updateSync: updateDocSync(dbContext, path),
      updateLatestSync: updateLatestDocSync(dbContext, path),
      updateOrAddWithId: updateOrAddDocumentWithId(dbContext, path),
      subscribe: subscribeToDocument(dbContext, path),
      delete: deleteDocument(dbContext, path),
    }),
    [dbContext, path]
  );
};
